import React, { memo } from 'react';
import { Box } from '@mui/material';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { checkBoxEnabledStyle, style } from 'components/PivotGrid/defaultStyle';
import { PivotFieldsAggridT } from 'business/definitions';
import { useFullScreen } from 'contexts/FullScreenContext';
import { useGetProjectsWithInscriptions } from '../hooks/getProjectsWithInscriptions';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import BudgetProjectsPivotGrid from './BudgetProjectsPivotGrid';

export const PROJECTS_PIVOT_GRID_CACHE_KEY = 'projects_pivot_grid_cache_key';

type Props = {
    budgetProposal: BudgetProposalT;
    settings: PivotFieldsAggridT | undefined;
    isLoading: boolean;
    filters: QueryFilterT[];
    setExporterToExcel: (arg1: () => void) => void;
    integratedSoftwares?: Set<string> | undefined;
};

const ProjectsPivotGrid = ({
    budgetProposal,
    settings,
    isLoading: isLoadingProp,
    filters,
    setExporterToExcel,
}: Props): React.ReactElement => {
    const { isFullScreen } = useFullScreen();
    const { projectKeys } = createBudgetQueryKeys();
    const projectCacheKey = projectKeys({
        filters: filters,
        budget_by: budgetProposal.by,
    });
    const { data, isFetching: isDataFetching } = useGetProjectsWithInscriptions(
        budgetProposal,
        filters,
        projectCacheKey,
    );

    const isLoading = isDataFetching || isLoadingProp;

    return (
        <Box
            sx={
                isFullScreen
                    ? { height: '75vh', minHeight: '26rem' }
                    : undefined
            }
        >
            <BudgetProjectsPivotGrid
                data={data}
                isLoading={isLoading}
                budgetProposal={budgetProposal}
                settings={settings}
                baseFilters={filters}
                setExporterToExcel={setExporterToExcel}
                sx={{
                    width: '100%',
                    '& .mui-project-pivot-grid': {
                        ...style,
                        ...checkBoxEnabledStyle,
                        '& .ag-header-cell-text': {
                            fontSize: '12px !important',
                        },
                        '& .ag-header-group-text': {
                            fontSize: '12px !important',
                        },
                        '& .ag-group-value': {
                            fontSize: '12px !important',
                            fontWeight: '500',
                        },
                    },
                    // Necessary (for now) so the grid can grow to its content
                    '& .ag-root-wrapper-body.ag-layout-normal': {
                        height: 'max-content',
                    },
                    /* Necessary (for now) so the grid without content still has room to display
                     * "No rows to show" without overlapping the header */
                    '& .ag-root.ag-layout-normal': {
                        minHeight: '10rem',
                    },
                }}
            />
        </Box>
    );
};

export default memo(ProjectsPivotGrid);
